import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../services';
import Loading from '../components/Loading';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, loading: cartLoading, fetchCart, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  
  const [shippingInfo, setShippingInfo] = useState({
    shipping_name: user?.name || '',
    shipping_email: user?.email || '',
    shipping_phone: user?.phone || '',
    shipping_address: user?.address || '',
    shipping_city: user?.city || '',
    shipping_postal_code: user?.postal_code || '',
    notes: '',
  });

  const steps = ['Shipping Information', 'Review Order', 'Order Placed'];

  const handleChange = (e) => {
    setShippingInfo({ ...shippingInfo, [e.target.name]: e.target.value });
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate shipping info
      if (!shippingInfo.shipping_name || !shippingInfo.shipping_email || 
          !shippingInfo.shipping_phone || !shippingInfo.shipping_address || 
          !shippingInfo.shipping_city) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(shippingInfo.shipping_email)) {
        setError('Please enter a valid email address');
        return;
      }

      setError('');
      setActiveStep(1);
    } else if (activeStep === 1) {
      handlePlaceOrder();
    }
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    setError('');
    
    try {
      const orderData = {
        ...shippingInfo
      };

      // For guest users, include cart items in the request
      if (!isAuthenticated) {
        if (!cart || !cart.items || cart.items.length === 0) {
          setError('Your cart is empty');
          setLoading(false);
          return;
        }
        
        orderData.cart_items = cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        }));
        
        console.log('Guest checkout - cart items:', orderData.cart_items);
        console.log('Full order data:', orderData);
      }

      const response = await orderService.create(orderData);
      setOrderNumber(response.data.order.order_number);
      setActiveStep(2);
      
      // Clear cart after successful order
      if (isAuthenticated) {
        await fetchCart(); // Server clears cart automatically
      } else {
        await clearCart(); // Clear local storage cart for guests
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  // Wait for cart to load before checking if empty
  if (cartLoading) return <Loading />;

  // Check if cart is truly empty (not just undefined during loading)
  if (cart && cart.items && cart.items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Alert severity="warning">
          Your cart is empty. Please add items before checkout.
        </Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate('/products')}
        >
          Browse Products
        </Button>
      </Container>
    );
  }

  if (activeStep === 2) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 100, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Order Placed Successfully!
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Your order number is: <strong>{orderNumber}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          We've sent a confirmation email to {shippingInfo.shipping_email}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/orders')}
          >
            View Orders
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/products')}
          >
            Continue Shopping
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        Checkout
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            {activeStep === 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Shipping Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Full Name"
                      name="shipping_name"
                      value={shippingInfo.shipping_name}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Email"
                      type="email"
                      name="shipping_email"
                      value={shippingInfo.shipping_email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Phone"
                      name="shipping_phone"
                      value={shippingInfo.shipping_phone}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Address"
                      name="shipping_address"
                      multiline
                      rows={2}
                      value={shippingInfo.shipping_address}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="City"
                      name="shipping_city"
                      value={shippingInfo.shipping_city}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      name="shipping_postal_code"
                      value={shippingInfo.shipping_postal_code}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Order Notes (Optional)"
                      name="notes"
                      multiline
                      rows={3}
                      value={shippingInfo.notes}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {activeStep === 1 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Review Your Order
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Shipping To:
                  </Typography>
                  <Typography variant="body2">{shippingInfo.shipping_name}</Typography>
                  <Typography variant="body2">{shippingInfo.shipping_address}</Typography>
                  <Typography variant="body2">
                    {shippingInfo.shipping_city} {shippingInfo.shipping_postal_code}
                  </Typography>
                  <Typography variant="body2">{shippingInfo.shipping_phone}</Typography>
                  <Typography variant="body2">{shippingInfo.shipping_email}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Order Items:
                </Typography>
                {cart.items.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {item.name} x {item.quantity}
                    </Typography>
                    <Typography variant="body2">
                      ${(item.price * item.quantity).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              {activeStep > 0 && activeStep < 2 && (
                <Button onClick={() => setActiveStep(0)} disabled={loading}>
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                sx={{ ml: 'auto' }}
              >
                {loading ? 'Processing...' : activeStep === 1 ? 'Place Order' : 'Continue'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Order Summary
            </Typography>
            
            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal</Typography>
                <Typography>${cart.total.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Shipping</Typography>
                <Typography color="success.main">FREE</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Total</Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ${cart.total.toFixed(2)}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              Payment Method: Cash on Delivery
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Checkout;
